import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';

export default function PWAInstallPrompt() {
  const { profile } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isSmall, setIsSmall] = useState(false);
  const [mode, setMode] = useState(null); // 'prompt' | 'instructions'
  const [isInstalled, setIsInstalled] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [dismissedUntilRefresh, setDismissedUntilRefresh] = useState(false);

  const computedMode = useMemo(() => {
    if (mode) return mode;
    return deferredPrompt ? 'prompt' : 'instructions';
  }, [mode, deferredPrompt]);

  useEffect(() => {
    const getIsInstalled = () => {
      try {
        const standalone =
          typeof window !== 'undefined' &&
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(display-mode: standalone)').matches;
        // iOS Safari “Add to Home Screen”
        const iosStandalone =
          typeof window !== 'undefined' &&
          typeof window.navigator !== 'undefined' &&
          window.navigator.standalone === true;
        return Boolean(standalone || iosStandalone);
      } catch {
        return false;
      }
    };

    const handleResize = () => {
      setIsSmall(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    // Initial installed state
    setIsInstalled(getIsInstalled());

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setMode('prompt');
      setBannerOpen(true);
    };
    const handleInstalled = () => {
      setIsInstalled(true);
      setBannerOpen(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);

    // Some browsers update display-mode without firing appinstalled reliably
    let mql;
    const onDisplayModeChange = () => setIsInstalled(getIsInstalled());
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      mql = window.matchMedia('(display-mode: standalone)');
      if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onDisplayModeChange);
      else if (typeof mql.addListener === 'function') mql.addListener(onDisplayModeChange);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
      if (mql) {
        if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', onDisplayModeChange);
        else if (typeof mql.removeListener === 'function') mql.removeListener(onDisplayModeChange);
      }
    };
  }, []);

  const isLoggedIn = Boolean(profile?.accessToken);

  // If installed, never show install UI again
  if (isInstalled) return null;
  // Only show when logged out
  if (isLoggedIn) return null;
  // If user closed it with ×, keep hidden until refresh
  if (dismissedUntilRefresh) return null;

  const canNativePrompt = computedMode === 'prompt' && Boolean(deferredPrompt);

  const onInstall = async () => {
    if (canNativePrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      // This event is single-use. After prompting, clear it.
      setDeferredPrompt(null);
      // If accepted, browsers will usually fire 'appinstalled' too, but we handle it here as well.
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setBannerOpen(false);
        return;
      }
      // Dismissed: keep the small install button available; just close the banner.
      setBannerOpen(false);
      return;
    }
    // Instructions mode: show how to install (HTTP, iOS, or when browser didn’t provide prompt yet)
    alert(
      isSmall
        ? 'To install this app:\n\nAndroid Chrome:\n1) Tap the browser menu (⋮)\n2) Tap "Add to Home screen" / "Install app"\n\niPhone Safari:\n1) Tap Share\n2) Tap "Add to Home Screen"'
        : 'To install this app:\n\n1) Open your browser menu\n2) Choose "Install app" / "Add to Home screen"'
    );
  };

  const onDismiss = () => {
    setBannerOpen(false);
  };

  const onCloseUntilRefresh = () => {
    setBannerOpen(false);
    setDismissedUntilRefresh(true);
  };

  const containerStyle = {
    position: 'fixed',
    left: 16,
    bottom: isSmall ? 'auto' : 16,
    top: isSmall ? 16 : 'auto',
    zIndex: 1000,
    maxWidth: 320,
  };

  const floatingBtnStyle = {
    position: 'fixed',
    left: 16,
    bottom: 16,
    zIndex: 999,
    background: '#0ea5e9',
    color: '#06121e',
    border: 0,
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    fontWeight: 800,
    boxShadow: '0 10px 30px rgba(0,0,0,.35)',
  };

  const cardStyle = {
    background: '#111827',
    color: '#e5e7eb',
    padding: 12,
    borderRadius: 12,
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    boxShadow: '0 10px 30px rgba(0,0,0,.45)',
    border: '1px solid rgba(255,255,255,0.08)',
    position: 'relative'
  };

  const primaryBtn = {
    background: '#0ea5e9',
    color: '#06121e',
    border: 0,
    padding: '8px 12px',
    borderRadius: 8,
    cursor: 'pointer',
    fontWeight: 700
  };

  const secondaryBtn = {
    background: 'transparent',
    color: '#e5e7eb',
    border: '1px solid rgba(229,231,235,0.2)',
    padding: '8px 12px',
    borderRadius: 8,
    cursor: 'pointer'
  };

  const title =
    computedMode === 'prompt'
      ? 'Install app?'
      : 'Add this app to your Home screen';
  const buttonLabel = computedMode === 'prompt' ? 'Install' : 'How to install';

  return (
    <>
      {/* Always keep an install option available until installed */}
      {!bannerOpen && (
        <button
          type="button"
          onClick={() => setBannerOpen(true)}
          style={floatingBtnStyle}
        >
          Install App
        </button>
      )}

      {bannerOpen && (
        <div style={containerStyle}>
          <div style={cardStyle}>
            <span style={{ fontWeight: 700 }}>{title}</span>
            <button onClick={onInstall} style={primaryBtn}>{buttonLabel}</button>
            <button onClick={onDismiss} style={secondaryBtn}>Later</button>
            <button
              onClick={onCloseUntilRefresh}
              style={{
                background: 'transparent',
                color: '#9ca3af',
                border: 'none',
                padding: '2px 6px',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold',
                position: 'absolute',
                top: '4px',
                right: '8px',
                borderRadius: '4px',
                lineHeight: '1'
              }}
              title="Close"
              type="button"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
}


